import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { WithRequired } from '@tanstack/react-query';
import {
  Empty,
  type EmptyProps,
  Table,
  type TableColumnsType,
  type TableColumnType,
  type TableProps,
  theme,
} from 'antd';
import type { PaginationProps } from 'antd/es/pagination/Pagination';
import type { ExpandableConfig, SorterResult } from 'antd/es/table/interface';
import { useFilters } from 'pos-client/hooks/useFilters.tsx';

import './PosTable.css';

export type PosTableProps<TData> = WithRequired<
  Omit<TableProps<TData>, 'pagination' | 'loading'>,
  'dataSource' | 'columns'
> & {
  isLoading?: boolean;
  paging?: PaginationProps;
  padding?: string | number;
  fePagination?: boolean;
  fePageSize?: number;
  showExpandAll?: boolean;
  noDataProps?: EmptyProps;
  detatchExpanded?: boolean;
  cellAlignment?: 'top' | 'middle' | 'bottom';
};

export function PosTable<TData extends object>({
  padding,
  isLoading = false,
  paging,
  fePagination,
  fePageSize,
  size = 'small',
  columns,
  showExpandAll = false,
  expandable,
  noDataProps,
  locale,
  detatchExpanded = false,
  cellAlignment,
  rowClassName,
  style,
  scroll,
  tableLayout = 'auto',
  ...props
}: PosTableProps<TData>) {
  const { token } = theme.useToken();
  const { t } = useTranslation();
  const { setFormFilters, formFilters } = useFilters();
  const [feDynamicPageSize, setFeDynamicPageSize] = useState(fePageSize ?? 10);
  const [expandRowKeys, setExpandRowKeys] = useState<string[]>([]);

  const handleSort = (sorter: SorterResult<TData>) => {
    setFormFilters({
      ...formFilters,
      sortBy: (sorter.column && (sorter.field as string)) ?? '',
      sortDirection: sorter.order ?? '',
    });
  };

  useEffect(() => {
    setFeDynamicPageSize(fePageSize ?? 10);
  }, [fePageSize, setFeDynamicPageSize]);

  const onChangeDynamicFePageSize = (_page: number, pageSize: number) => {
    setFeDynamicPageSize(pageSize);
  };

  const expandAllHeader = useMemo(
    () => (
      <button
        style={{ marginLeft: 7, right: 4 }}
        className={`ant-table-row-expand-icon ant-table-row-expand-icon-${
          expandRowKeys.length === props.dataSource.length
            ? 'expanded'
            : 'collapsed'
        }`}
        onClick={() =>
          setExpandRowKeys(
            expandRowKeys.length === 0 ||
              expandRowKeys.length !== props.dataSource.length
              ? props.dataSource.map((data) =>
                  Object.hasOwn(data, 'key')
                    ? (data['key' as keyof typeof data] as string)
                    : ''
                )
              : []
          )
        }
      />
    ),
    [expandRowKeys, props.dataSource]
  );

  const expandAllProps: ExpandableConfig<TData> = useMemo(() => {
    return {
      columnTitle: expandAllHeader,
      expandedRowKeys: expandRowKeys,
      onExpand: (expanded, obj) =>
        setExpandRowKeys((rowKeys) =>
          Object.hasOwn(obj, 'key')
            ? expanded
              ? [...rowKeys, obj['key' as keyof typeof obj] as string]
              : rowKeys.filter(
                  (rKey) => rKey !== (obj['key' as keyof typeof obj] as string)
                )
            : []
        ),
    };
  }, [expandRowKeys, expandAllHeader]);

  const cellColumnAlign: TableColumnType['onCell'] = useCallback(
    () => ({
      className:
        cellAlignment !== 'middle' ? `cell-align-${cellAlignment}` : undefined,
    }),
    [cellAlignment]
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tableColumns: TableColumnsType<any> = useMemo(
    () =>
      columns.map(({ onCell, ...column }) => ({
        ...column,
        onCell: (data, index) => ({
          ...(onCell && { ...onCell(data, index) }),
          ...cellColumnAlign(data, index),
        }),
      })),
    [cellColumnAlign, columns]
  );

  return (
    <Table
      {...props}
      columns={tableColumns}
      tableLayout={tableLayout}
      rootClassName={fePagination ? 'fe-paginated-table' : props.rootClassName}
      pagination={
        fePagination || paging
          ? {
              size: 'default',
              position: ['bottomCenter'],
              ...paging,
              ...(fePagination && {
                pageSize: feDynamicPageSize,
                onChange: onChangeDynamicFePageSize,
              }),
            }
          : false
      }
      scroll={{
        x: true,
        ...scroll,
      }}
      size={size}
      rowClassName={rowClassName}
      loading={
        isLoading
          ? {
              tip: `${t('common.states.loading')}...`,
              size: 'large',
              children: <div>{`${t('common.states.loading')}...`}</div>,
            }
          : false
      }
      locale={{
        ...(!isLoading &&
          noDataProps && {
            emptyText: (
              <Empty
                {...noDataProps}
                style={{
                  paddingBottom: token.paddingMD,
                  paddingTop: token.paddingMD,
                  ...noDataProps.style,
                }}
                image={noDataProps?.image ?? Empty.PRESENTED_IMAGE_SIMPLE}
                styles={{
                  image: {
                    height: 50,
                    paddingBottom: token.paddingXS,
                    ...noDataProps.styles?.image,
                  },
                }}
              />
            ),
          }),
      }}
      onChange={(pagination, filters, sorter, extras) => {
        if (props.onChange) {
          props.onChange(pagination, filters, sorter, extras);
        } else {
          handleSort(sorter as SorterResult<TData>);
        }
      }}
      style={{
        /*  Padding style copied over from existing <section /> wrapper
            No idea why it was implemented like this but would need to correct margin on every page
            Added to FE tech debt - 16/09/25 */
        marginTop: padding !== undefined ? 0 : '8px',
        padding,
        paddingBottom: 10,
        ...style,
      }}
      expandable={expandable}
      {...(showExpandAll && {
        expandable: {
          expandedRowClassName: () =>
            detatchExpanded ? `detatch-row ${size}` : '',
          ...expandable,
          ...expandAllProps,
        },
      })}
    />
  );
}
